import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Avatar, Box, InputBase, List, ListItemButton, Typography } from "@mui/material";
import {
  CHAT_ROOM_ALL,
  formatBytes,
  type ChatOverviewResponse,
  type ChatTarget,
  type PresenceEntry,
  type PresenceStatus,
} from "@xop/dashboard-shared";
import { useMemo, useState } from "react";

import { UserAvatar } from "../../components/UserAvatar.js";
import { listTimestamp } from "./format.js";

/**
 * WhatsApp-style conversation list: a search box, the pinned "All Staff" room,
 * then every teammate as a 1:1 conversation row — avatar (with live presence),
 * name, last-message preview, timestamp, and an unread pill. Presence is
 * merged from the realtime `online` set; everything else comes from overview.
 */
export function ChatSidebar({
  overview,
  online,
  selectedKey,
  onSelect,
}: {
  overview: ChatOverviewResponse | undefined;
  online: PresenceEntry[];
  selectedKey: string;
  onSelect: (target: ChatTarget) => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const presence = useMemo(() => {
    const map = new Map<number, PresenceStatus>();
    for (const e of online) map.set(e.user_id, e.status);
    return map;
  }, [online]);

  const q = query.trim().toLowerCase();
  const contacts = (overview?.contacts ?? []).filter(
    (c) => !q || c.display_name.toLowerCase().includes(q),
  );
  const roomMatches = !q || (overview?.room.label ?? "All Staff").toLowerCase().includes(q);

  const roomPreview = overview?.room.last_message
    ? overview.room.last_message_sender
      ? `${overview.room.last_message_sender}: ${overview.room.last_message}`
      : overview.room.last_message
    : "Team-wide broadcast channel";

  return (
    <Box
      data-testid="chat-sidebar"
      sx={{
        width: { xs: "100%", sm: 300 },
        flexShrink: 0,
        borderRight: { sm: 1 },
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Search */}
      <Box sx={{ p: 1.25, borderBottom: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 4,
            bgcolor: "surfaceSoft",
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search teammates"
            fullWidth
            inputProps={{ "aria-label": "Search teammates", "data-testid": "chat-search" }}
            sx={{ fontSize: 14 }}
          />
        </Box>
      </Box>

      <List disablePadding sx={{ overflowY: "auto", flex: 1, py: 0 }}>
        {roomMatches && (
          <ConversationRow
            avatar={
              <Avatar sx={{ width: 44, height: 44, bgcolor: "chat.accent", color: "#fff" }}>
                <GroupsRoundedIcon />
              </Avatar>
            }
            name={overview?.room.label ?? "All Staff"}
            preview={roomPreview}
            time={listTimestamp(overview?.room.last_message_at ?? null)}
            unread={overview?.room.unread ?? 0}
            selected={selectedKey === `room:${CHAT_ROOM_ALL}`}
            onClick={() => onSelect({ kind: "room", room: CHAT_ROOM_ALL })}
            testid="chat-room-all"
          />
        )}

        {contacts.map((c) => {
          const state = presence.get(c.user_id) ?? "offline";
          const preview = c.last_message
            ? c.last_message_from_me
              ? `You: ${c.last_message}`
              : c.last_message
            : "Tap to start a conversation";
          return (
            <ConversationRow
              key={c.user_id}
              avatar={<UserAvatar name={c.display_name} userId={c.user_id} size={44} presence={state} />}
              name={c.display_name}
              preview={preview}
              time={listTimestamp(c.last_message_at)}
              unread={c.unread}
              selected={selectedKey === `dm:${c.user_id}`}
              onClick={() => onSelect({ kind: "dm", user_id: c.user_id })}
              testid={`chat-dm-${c.user_id}`}
            />
          );
        })}

        {overview && contacts.length === 0 && !roomMatches && (
          <Typography sx={{ px: 2, py: 2, fontSize: 13, color: "text.secondary" }}>
            No teammates match “{query}”.
          </Typography>
        )}
      </List>

      {overview?.storage && (
        <StorageMeter used={overview.storage.used_bytes} max={overview.storage.max_bytes} />
      )}
    </Box>
  );
}

/** A slim "file storage X / Y used" meter so the team can see how much of
 * the attachment cap is consumed and never overflow the box. */
function StorageMeter({ used, max }: { used: number; max: number }): JSX.Element {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  return (
    <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>File storage</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {formatBytes(used)} / {formatBytes(max)}
        </Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "surfaceSoft", overflow: "hidden" }}>
        <Box
          data-testid="chat-storage-bar"
          sx={{
            height: "100%",
            width: `${pct}%`,
            bgcolor: pct > 90 ? "error.main" : "chat.accent",
            transition: "width 300ms",
          }}
        />
      </Box>
    </Box>
  );
}

function ConversationRow({
  avatar,
  name,
  preview,
  time,
  unread,
  selected,
  onClick,
  testid,
}: {
  avatar: JSX.Element;
  name: string;
  preview: string;
  time: string;
  unread: number;
  selected: boolean;
  onClick: () => void;
  testid: string;
}): JSX.Element {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      data-testid={testid}
      sx={{
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        "&.Mui-selected": {
          bgcolor: "action.selected",
          "&:hover": { bgcolor: "action.selected" },
        },
      }}
    >
      {avatar}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 15,
              fontWeight: unread > 0 ? 700 : 500,
              color: "text.primary",
            }}
          >
            {name}
          </Typography>
          {time && (
            <Typography
              sx={{
                fontSize: 11,
                flexShrink: 0,
                color: unread > 0 ? "chat.accent" : "text.disabled",
                fontWeight: unread > 0 ? 700 : 400,
              }}
            >
              {time}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            {preview}
          </Typography>
          {unread > 0 && (
            <Box
              data-testid="chat-unread-badge"
              sx={{
                ml: "auto",
                flexShrink: 0,
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: 10,
                bgcolor: "chat.accent",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {unread > 99 ? "99+" : unread}
            </Box>
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
}
